import { type VariantProps, cva } from 'class-variance-authority';
import { type InputHTMLAttributes, type TextareaHTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

/**
 * Text input primitive. Sits on `surface-2` so it reads as a recessed
 * channel inside cards (which sit on `surface`). Focus borrows from the
 * design system's `accent-ring` token: 1px stroke darkens to `accent`,
 * plus a 3px translucent ring beyond.
 */
const inputBase =
  'w-full rounded-md border border-border bg-surface-2 text-fg font-ui placeholder:text-fg-3 transition-colors duration-200 ease-out focus:outline-none focus:border-brand focus:ring-2 focus:ring-ring/35 disabled:cursor-not-allowed disabled:opacity-60';

const inputVariants = cva(inputBase, {
  variants: {
    inputSize: {
      sm: 'h-8 px-3 text-xs',
      md: 'h-9 px-3.5 text-base',
      lg: 'h-11 px-4 text-md',
    },
  },
  defaultVariants: { inputSize: 'md' },
});

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, inputSize, type = 'text', ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(inputVariants({ inputSize, className }))}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(inputBase, 'min-h-[88px] resize-y px-3.5 py-2.5 text-base', className)}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

export { inputVariants };
